(function() {
  'use strict';

  const MOBILE_BREAKPOINT = 750;
  const MOBILE_MODAL_EXTRA_SPACE = 48;
  const POPOVER_OFFSET = 16;
  const SECTION_SELECTOR = '.rb-interactive-media[data-section-id]:not(.rb-mediatext__interactive-dots)';
  const instances = new Map();

  const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;
  const isDesignMode = () => window.Shopify && window.Shopify.designMode;
  const closeAllOpenContent = () => instances.forEach(i => i.closeCurrentContent());

  class InteractiveMedia {
    constructor(section) {
      this.section = section;
      this.currentOpenContent = null;
      this.video = section.querySelector('.rb-interactive-media__video');
      this.dots = Array.from(section.querySelectorAll('.rb-interactive-media__dot'));
      this.timeDisplay = section.querySelector('.rb-interactive-media__time-display');
      this.playPauseBtn = section.querySelector('.rb-interactive-media__playPauseBtn');
      this.muteUnmuteBtn = section.querySelector('.rb-interactive-media__muteUnmuteBtn');
      this.wasPlayingBeforePause = false;

      const sectionId = section.getAttribute('data-section-id');
      instances.set(sectionId, this);
      section.addEventListener('click', (e) => this.handleDotClick(e));

      if (this.video && !isDesignMode()) {
        this.initVideoTracking();
        this.initVideoControls();
      } else {
        this.showAllDots();
      }
    }

    showAllDots() {
      this.dots.forEach(dot => dot.classList.add('rb-interactive-media__dot--visible'));
    }

    query(selector) {
      return this.section.querySelector(selector);
    }

    initVideoTracking() {
      const update = () => {
        this.updateDotVisibility();
        this.updateTimeDisplay();
      };

      if (this.video.readyState >= 2) {
        update();
      } else {
        this.video.addEventListener('loadedmetadata', update, { once: true });
      }

      ['timeupdate', 'ended', 'seeked'].forEach(event => {
        this.video.addEventListener(event, update);
      });
    }

    updateDotVisibility() {
      if (isDesignMode()) {
        this.showAllDots();
        return;
      }

      const currentTime = this.video.currentTime;
      this.dots.forEach(dot => {
        const startTime = parseFloat(dot.getAttribute('data-start-time')) || 0;
        const duration = parseFloat(dot.getAttribute('data-duration')) || 5;
        dot.classList.toggle('rb-interactive-media__dot--visible',
          currentTime >= startTime && currentTime < startTime + duration);
      });
    }

    updateTimeDisplay() {
      if (this.timeDisplay) {
        this.timeDisplay.textContent = `${Math.floor(this.video.currentTime)}s`;
      }
    }

    initVideoControls() {
      const updatePlayState = () => this.playPauseBtn?.classList.toggle('playing', !this.video.paused);
      const updateMuteState = () => this.muteUnmuteBtn?.classList.toggle('muted', this.video.muted);

      this.playPauseBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.video.paused ? this.video.play().catch(() => {}) : this.video.pause();
      });

      this.muteUnmuteBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.video.muted = !this.video.muted;
      });

      ['play', 'pause'].forEach(event => this.video.addEventListener(event, updatePlayState));
      this.video.addEventListener('volumechange', updateMuteState);

      updatePlayState();
      updateMuteState();
    }

    calculatePopoverPosition(button, content) {
      const container = button.closest('.rb-interactive-media__wrapper');
      if (!container) return { top: 'auto', left: 'auto', bottom: 'auto', right: 'auto' };

      content.style.visibility = 'hidden';
      content.style.display = 'block';
      const contentRect = content.getBoundingClientRect();
      content.style.visibility = '';
      content.style.display = '';

      const containerRect = container.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const { innerWidth: vw, innerHeight: vh } = window;
      const { width: cw, height: ch } = contentRect;
      const offset = POPOVER_OFFSET;
      const minSpace = ch + offset;
      const minWidth = cw + offset;

      const position = { top: 'auto', left: 'auto', bottom: 'auto', right: 'auto' };

      if (vh - buttonRect.bottom >= minSpace) {
        position.top = `${buttonRect.bottom - containerRect.top + offset}px`;
      } else if (buttonRect.top >= minSpace) {
        position.bottom = `${containerRect.bottom - buttonRect.top + offset}px`;
      } else {
        const topPos = (buttonRect.top + buttonRect.height / 2) - containerRect.top - ch / 2;
        position.top = `${Math.max(0, Math.min(topPos, containerRect.height - ch))}px`;
      }

      if (vw - buttonRect.right >= minWidth) {
        position.left = `${Math.max(0, Math.min(buttonRect.left - containerRect.left, containerRect.width - cw))}px`;
      } else if (buttonRect.left >= minWidth) {
        position.right = `${containerRect.right - buttonRect.right}px`;
      } else {
        const leftPos = (buttonRect.left + buttonRect.width / 2) - containerRect.left - cw / 2;
        position.left = `${Math.max(0, Math.min(leftPos, containerRect.width - cw))}px`;
      }

      return position;
    }

    openContent(dotId) {
      const content = this.query(`[data-dot-id="${dotId}"].rb-interactive-media__dot-content`);
      const button = this.query(`[data-dot-id="${dotId}"].rb-interactive-media__dot`);
      if (!content || !button) return;

      closeAllOpenContent();

      if (this.video && !this.video.paused) {
        this.video.pause();
        this.wasPlayingBeforePause = true;
      }

      if (isMobile()) {
        const wrapper = button.closest('.rb-interactive-media__wrapper');
        if (!wrapper) return;
        content.classList.add('rb-interactive-media__dot-content--modal');
        content.style.maxHeight = `${wrapper.getBoundingClientRect().height + MOBILE_MODAL_EXTRA_SPACE}px`;
      } else {
        content.classList.add('rb-interactive-media__dot-content--popover');
        Object.assign(content.style, this.calculatePopoverPosition(button, content));
      }

      content.setAttribute('aria-hidden', 'false');
      this.currentOpenContent = content;
    }

    closeContent(dotId) {
      const content = this.query(`[data-dot-id="${dotId}"].rb-interactive-media__dot-content`);
      if (!content) return;

      content.classList.remove('rb-interactive-media__dot-content--modal', 'rb-interactive-media__dot-content--popover');
      content.setAttribute('aria-hidden', 'true');
      ['top', 'left', 'bottom', 'right', 'transform', 'maxHeight'].forEach(prop => content.style[prop] = '');

      if (this.currentOpenContent === content) {
        this.currentOpenContent = null;
        if (this.video?.paused && this.wasPlayingBeforePause) {
          this.video.play().catch(() => {});
          this.wasPlayingBeforePause = false;
        }
      }
    }

    closeCurrentContent() {
      const dotId = this.currentOpenContent?.getAttribute('data-dot-id');
      if (dotId) this.closeContent(dotId);
    }

    handleDotClick(e) {
      if (e.target.closest('.rb-interactive-media__actions')) return;
      const button = e.target.closest('.rb-interactive-media__dot');
      if (!button || !this.section.contains(button) || !button.classList.contains('rb-interactive-media__dot--visible')) return;
      const dotId = button.getAttribute('data-dot-id');
      if (!dotId) return;
      const content = this.query(`[data-dot-id="${dotId}"].rb-interactive-media__dot-content`);
      if (!content) return;
      const isOpen = this.currentOpenContent === content && content.getAttribute('aria-hidden') === 'false';
      isOpen ? this.closeContent(dotId) : this.openContent(dotId);
    }
  }

  function handleOutsideClick(e) {
    const clickedSection = e.target.closest(SECTION_SELECTOR);
    const content = e.target.closest('.rb-interactive-media__dot-content');
    const button = e.target.closest('.rb-interactive-media__dot');

    instances.forEach(instance => {
      if (!instance.section.contains(e.target)) return;

      if (isMobile() && content?.classList.contains('rb-interactive-media__dot-content--modal')) {
        if (e.target === content) {
          instance.closeContent(content.getAttribute('data-dot-id'));
        }
      } else if (!content && !button && instance.currentOpenContent) {
        instance.closeCurrentContent();
      }
    });

    if (!clickedSection) closeAllOpenContent();
  }

  function init(root = document) {
    root.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      const key = section.getAttribute('data-section-id');
      if (!key) return;
      if (instances.has(key)) instances.delete(key);
      new InteractiveMedia(section);
    });
  }

  function boot() {
    init(document);
    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', (e) => e.key === 'Escape' && closeAllOpenContent());
    window.addEventListener('resize', closeAllOpenContent);

    if (isDesignMode()) {
      document.addEventListener('shopify:section:load', (e) => init(e.target));
      document.addEventListener('shopify:section:unload', (e) => {
        const sectionId = e.detail?.sectionId;
        if (sectionId) instances.delete(sectionId);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
